import { ConfigStateService } from '@abp/ng.core';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CustomSettingsService } from '../proxy/custom-settings.service';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  ADD_ON_AI_FEATURE_MODEL,
  ADD_ON_AI_FEATURE_LLM_CONFIGURATION,
  ADD_ON_AI_FEATURE_MODEL_NAME,
  ADDON_AI_FIELD_NAMES,
  ADD_ON_AI_FEATURE_REASONING_EFFORT,
} from './addon-ai-features-settings.consts';
import { CustomSettingsDto } from '../proxy/custom-settings.model';
import { AddOnAIFeaturesSettingsService } from './proxy/addon-ai-features-settings.service';
import {
  LlmConfigurationProviderModelsDto,
  LlmConfigurationSummaryDto,
  ReasoningEffort,
} from 'src/app/integrations/proxy/integration.model';
import { finalize } from 'rxjs/operators';
import { CUSTOM_MODEL } from 'src/app/ai-agents/proxy/ai-agents.consts';
import { forkJoin } from 'rxjs';
import { IntegrationService } from 'src/app/integrations/proxy/integration.service';

@Component({
  selector: 'cai-addon-ai-features-settings',
  templateUrl: './addon-ai-features-settings.component.html',
  styleUrls: ['./addon-ai-features-settings.component.scss'],
  standalone: false,
})
export class AddOnAIFeaturesSettingsComponent implements OnInit {
  form: UntypedFormGroup;
  llmConfigurationOptions: LlmConfigurationSummaryDto[] = [];
  groupedModelList: LlmConfigurationProviderModelsDto[] = [];
  addonAiFeatureFieldNames = ADDON_AI_FIELD_NAMES;
  isLoading = false;

  constructor(
    private config: ConfigStateService,
    private fb: UntypedFormBuilder,
    private settingsService: CustomSettingsService,
    private toasterService: ToasterService,
    private addOnAIFeaturesSettingsService: AddOnAIFeaturesSettingsService,
    private integrationService: IntegrationService,
  ) {}

  ngOnInit(): void {
    this.loadLlmConfigurationSettings();
  }

  private loadLlmConfigurationSettings() {
    forkJoin({
      addOnAiFeatureOptions: this.addOnAIFeaturesSettingsService.getAddOnAiFeatureSettingOptions(),
      suggestedModels: this.integrationService.getSuggestedModels(),
    }).subscribe({
      next: ({ addOnAiFeatureOptions, suggestedModels }) => {
        const llmConfigurationSettings = addOnAiFeatureOptions.llmConfigurationSettings.map(
          (opt) => ({ ...opt }),
        );

        this.llmConfigurationOptions = llmConfigurationSettings.map((configuration) => ({
          id: configuration.id,
          name: configuration.name,
          provider: String(configuration.provider),
        }));

        const availableProviderSet = new Set(llmConfigurationSettings.map((c) => c.provider));
        this.groupedModelList = suggestedModels.filter((modelGroup) =>
          availableProviderSet.has(modelGroup.provider),
        );

        this.buildForm();
      },
      error: (error) => {
        console.error('Failed to load add-on AI feature setting options:', error);
        this.buildForm();
      },
    });
  }

  private buildForm() {
    if (this.form) {
      this.updateFormValues();
      return;
    }

    this.createForm();
    this.updateFormValues();
  }

  private createForm() {
    this.form = this.fb.group({
      [this.addonAiFeatureFieldNames.llmConfiguration]: [null, [Validators.required]],
      [this.addonAiFeatureFieldNames.llmModel]: [null],
      [this.addonAiFeatureFieldNames.llmCustomModel]: [null],
      [this.addonAiFeatureFieldNames.reasoningEffort]: [null],
    });
  }

  private updateFormValues() {
    const savedLlmConfiguration = this.config.getSetting(ADD_ON_AI_FEATURE_LLM_CONFIGURATION);
    const savedModel = this.config.getSetting(ADD_ON_AI_FEATURE_MODEL)?.trim() || null;
    const savedCustomModelName =
      this.config.getSetting(ADD_ON_AI_FEATURE_MODEL_NAME)?.trim() || null;
    const savedReasoningEffort = this.config.getSetting(ADD_ON_AI_FEATURE_REASONING_EFFORT) || null;

    const llmConfigurationId = savedLlmConfiguration
      ? this.getConfigurationId(savedLlmConfiguration)
      : null;
    const reasoningEffortToUse = savedReasoningEffort
      ? (Number(savedReasoningEffort) as ReasoningEffort)
      : null;

    this.form.patchValue(
      {
        [this.addonAiFeatureFieldNames.llmConfiguration]: llmConfigurationId,
        [this.addonAiFeatureFieldNames.llmModel]: savedModel,
        [this.addonAiFeatureFieldNames.llmCustomModel]: savedCustomModelName,
        [this.addonAiFeatureFieldNames.reasoningEffort]: reasoningEffortToUse,
      },
      { emitEvent: false },
    );
  }

  submitForm() {
    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;

    const llmModelValue = this.form.get(this.addonAiFeatureFieldNames.llmModel)?.value?.trim();
    const llmCustomModelValue = this.form
      .get(this.addonAiFeatureFieldNames.llmCustomModel)
      ?.value?.trim();
    const isCustomModelSelected = llmModelValue === CUSTOM_MODEL;
    const selectedLlmConfiguration = this.form.get(
      this.addonAiFeatureFieldNames.llmConfiguration,
    )?.value;
    const selectedLlmConfigurationId = selectedLlmConfiguration
      ? this.getConfigurationId(selectedLlmConfiguration)
      : null;
    const reasoningEffortValue = this.form.get(
      this.addonAiFeatureFieldNames.reasoningEffort,
    )?.value;
    const settings: CustomSettingsDto[] = [
      {
        name: ADD_ON_AI_FEATURE_LLM_CONFIGURATION,
        value: selectedLlmConfigurationId,
      },
      {
        name: ADD_ON_AI_FEATURE_MODEL,
        value: llmModelValue ? llmModelValue : null,
      },
      {
        name: ADD_ON_AI_FEATURE_MODEL_NAME,
        value: isCustomModelSelected && llmCustomModelValue ? llmCustomModelValue : null,
      },
      {
        name: ADD_ON_AI_FEATURE_REASONING_EFFORT,
        value: reasoningEffortValue != null ? String(reasoningEffortValue) : null,
      },
    ];

    this.settingsService
      .save(settings)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.toasterService.success('::CustomSettings:Save:Success');
          this.updateFormValues();
        },
        error: (error) => {
          const businessExceptionErrorCodePrefix = 'Raven:';

          // If the error is caused by a business exception, then the related error message will be shown
          // via global error handling popup, so no need to display an additional error toaster here.
          if (!error?.error?.error?.code?.startsWith(businessExceptionErrorCodePrefix)) {
            this.toasterService.error('::CustomSettings:Save:Error');
            console.log(error);
          }
        },
      });
  }

  getConfigurationId(configNameOrId: string): string {
    const configByName = this.llmConfigurationOptions.find((c) => c.name === configNameOrId);
    return configByName ? configByName.id : configNameOrId;
  }
}
